import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Account } from '../types';
import { accountsAPI } from '../services/api';
import { formatCurrency, formatDate, getAssetCategories, getHighLevelCategory, getHighLevelCategoryFromWealthiniCategory } from '../utils/formatters';
import { convertCurrency } from '../services/currencyService';
import AccountHistory from '../components/AccountHistory';
import AccountValue from '../components/AccountValue';
import UpdateValueModal from '../components/UpdateValueModal';
import { useAuth } from '../contexts/AuthContext';
import { useAccountFilter } from '../contexts/AccountFilterContext';
import AddAssetModal from '../components/AddAssetModal';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const Assets: React.FC = () => {
  const { user } = useAuth();
  const { accountFilter } = useAccountFilter();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [viewingHistory, setViewingHistory] = useState<Account | null>(null);
  const [updatingValueFor, setUpdatingValueFor] = useState<Account | null>(null);
  const historyScrollRef = useRef<number>(0);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedPlaidAccount, setSelectedPlaidAccount] = useState<any>(null);
  const [selectedManualAccountId, setSelectedManualAccountId] = useState<number | ''>('');
  const [linkingLoading, setLinkingLoading] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedAccountForTransactions, setSelectedAccountForTransactions] = useState<Account | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionDateRange, setTransactionDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });

  // #region agent log
  const logDebug = (payload: {
    runId: string;
    hypothesisId: string;
    location: string;
    message: string;
    data?: any;
  }) => {
    fetch('http://127.0.0.1:7475/ingest/6fd3aedb-26df-4ed8-84a4-7731935e3772', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': '58d8b5',
      },
      body: JSON.stringify({
        sessionId: '58d8b5',
        timestamp: Date.now(),
        ...payload,
      }),
    }).catch(() => {});
  };
  // #endregion

  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    banking: true,
    realEstate: true,
    vehicles: true,
    preciousMetals: true,
    personalProperty: true,
    businessAssets: true
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const [unlinkedPlaidAccounts, setUnlinkedPlaidAccounts] = useState<any[]>([]);

  // Helper functions to categorize assets into buckets
  const getAssetBucket = (account: Account): string => {
    const category = account.category?.toLowerCase() || '';
    const highLevelCategory = getHighLevelCategoryFromWealthiniCategory(account.category || '');
    
    // Banking & Investing (includes both personal and business)
    if (['checking account', 'savings account', 'money market', 'cd', 'cash', 
         'brokerage account', 'ira', '401(k)', 'rrsp', 'tfsa', 'resp', 'pension', 'investment accounts',
         'business checking', 'business savings', 'business money market', 'business investments'].includes(category)) {
      return 'banking';
    }
    
    // Real Estate
    if (['real estate', 'business real estate'].includes(category)) {
      return 'realEstate';
    }
    
    // Vehicles
    if (['vehicles', 'business vehicles'].includes(category)) {
      return 'vehicles';
    }
    
    // Precious Metals
    if (['precious metals', 'gold', 'silver', 'platinum', 'palladium'].includes(category)) {
      return 'preciousMetals';
    }
    
    // Personal Property
    if (['personal property'].includes(category)) {
      return 'personalProperty';
    }
    
    // Business Assets (non-banking/investing)
    if (account.account_type === 'business' && 
        ['equipment & machinery', 'inventory', 'accounts receivable', 'intellectual property', 'other business assets'].includes(category)) {
      return 'businessAssets';
    }
    
    // Cryptocurrency
    if (['cryptocurrency'].includes(category)) {
      return 'cryptocurrency';
    }
    
    // Default to Other Assets
    return 'other';
  };



  const getBucketTotal = (accounts: Account[]): number => {
    return accounts.reduce((total, account) => {
      // Handle current_value which might be undefined, null, or NaN
      let value = 0;
      
      if (account.current_value !== undefined && account.current_value !== null) {
        value = Number(account.current_value);
      }
      
      // Ensure we have a valid number
      if (isNaN(value)) {
        value = 0;
      }
      
      return total + value;
    }, 0);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountsAPI.getAccounts({ type: 'asset' });
      setAccounts(data);
      
      // Also fetch unlinked Plaid accounts
      const linkedAccounts = await accountsAPI.getLinkedAccounts();
      const unlinked = linkedAccounts.filter((acc: any) => 
        !acc.manual_account_id && acc.account_type === 'asset'
      );
      setUnlinkedPlaidAccounts(unlinked);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      try {
        await accountsAPI.deleteAccount(id);
        await fetchAccounts();
        
        // Restore scroll position after re-render
        setTimeout(() => {
          window.scrollTo(0, scrollY);
        }, 0);
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }
  };

  const handleLinkPlaidAccount = (plaidAccount: any) => {
    setSelectedPlaidAccount(plaidAccount);
    setSelectedManualAccountId('');
    setShowLinkModal(true);
  };

  const handleLinkAccount = async () => {
    if (!selectedManualAccountId || !selectedPlaidAccount) {
      alert('Please select a manual account to link to.');
      return;
    }

    try {
      setLinkingLoading(true);
      await accountsAPI.linkPlaidToManual(selectedPlaidAccount.id, selectedManualAccountId as number);
      
      // Refresh the accounts to show the updated status
      await fetchAccounts();
      
      setShowLinkModal(false);
      setSelectedPlaidAccount(null);
      setSelectedManualAccountId('');
    } catch (error) {
      console.error('Error linking account:', error);
      alert('Failed to link account. Please try again.');
    } finally {
      setLinkingLoading(false);
    }
  };

  const handleUnlinkAccount = async (account: Account) => {
    if (!account.linked_account_id) {
      alert('This account is not linked to any Plaid account.');
      return;
    }

    if (window.confirm(`Are you sure you want to unlink "${account.name}" from its Plaid account? This will not delete the account, just remove the connection.`)) {
      try {
        await accountsAPI.unlinkPlaidAccount(account.linked_account_id);
        
        // Refresh the accounts to show the updated status
        await fetchAccounts();
      } catch (error) {
        console.error('Error unlinking account:', error);
        alert('Failed to unlink account. Please try again.');
      }
    }
  };

  // Filter accounts based on accountFilter using useMemo to prevent infinite loops
  const filteredAccounts = useMemo(() => {
    if (accountFilter === 'all') return accounts;
    return accounts.filter(account => account.account_type === accountFilter);
  }, [accounts, accountFilter]);

  const getAccountsByBucket = useCallback(() => {
    const buckets: {[key: string]: Account[]} = {
      banking: [],
      realEstate: [],
      vehicles: [],
      preciousMetals: [],
      personalProperty: [],
      businessAssets: [],
      cryptocurrency: [],
      other: []
    };
    
    filteredAccounts.forEach(account => {
      const bucket = getAssetBucket(account);
      buckets[bucket].push(account);
    });
    
    return buckets;
  }, [filteredAccounts]);

  const handleViewTransactions = async (account: Account) => {
    if (!account.linked_account_id) {
      alert('This account is not linked to any Plaid account.');
      return;
    }

    setSelectedAccountForTransactions(account);
    setShowTransactionsModal(true);
    await fetchTransactions(account.linked_account_id);
  };

  const fetchTransactions = async (linkedAccountId: number) => {
    try {
      setTransactionsLoading(true);
      // #region agent log
      logDebug({
        runId: 'pre-fix',
        hypothesisId: 'date-picker-range',
        location: 'Assets.tsx:fetchTransactions',
        message: 'Fetching transactions with date range',
        data: {
          linkedAccountId,
          startDate: transactionDateRange.startDate,
          endDate: transactionDateRange.endDate,
        },
      });
      // #endregion
      const response = await accountsAPI.getAccountTransactions(
        linkedAccountId,
        transactionDateRange.startDate,
        transactionDateRange.endDate
      );
      setTransactions(response.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      // #region agent log
      logDebug({
        runId: 'pre-fix',
        hypothesisId: 'date-picker-range',
        location: 'Assets.tsx:fetchTransactions',
        message: 'Error fetching transactions',
        data: {
          errorMessage: error instanceof Error ? error.message : String(error),
          linkedAccountId,
          startDate: transactionDateRange.startDate,
          endDate: transactionDateRange.endDate,
        },
      });
      // #endregion
      alert('Failed to fetch transactions. Please try again.');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const openModal = () => {
    setEditingAccount(null);
    setShowModal(true);
  };

  const userPreferredCurrency = user?.default_currency || 'CAD';
  const [totalValue, setTotalValue] = useState<number>(0);
  const [totalValueLoading, setTotalValueLoading] = useState<boolean>(false);

  // Calculate total value with currency conversion
  useEffect(() => {
    let isMounted = true;
    
    const calculateTotalValue = async () => {
      try {
        setTotalValueLoading(true);
        let total = 0;
        
        if (filteredAccounts.length === 0) {
          if (isMounted) {
            setTotalValue(0);
            setTotalValueLoading(false);
          }
          return;
        }

        // Add manual accounts
        for (const account of filteredAccounts) {
          if (!isMounted) break; // Check if component is still mounted
          
          const value = parseFloat(account.current_value?.toString() || '0') || 0;
          const accountCurrency = account.currency || 'CAD';
          
          if (accountCurrency === userPreferredCurrency) {
            total += value;
          } else {
            try {
              // Convert to user's preferred currency
              const convertedValue = await convertCurrency(value, accountCurrency, userPreferredCurrency);
              if (isMounted) {
                total += convertedValue;
              }
            } catch (conversionError) {
              console.error('Currency conversion failed for account:', account.id, conversionError);
              // Use original value if conversion fails
              if (isMounted) {
                total += value;
              }
            }
          }
        }
        
        // Add unlinked Plaid accounts (assuming USD for now) - only if showing all accounts
        if (accountFilter === 'all' && isMounted) {
          for (const plaidAccount of unlinkedPlaidAccounts) {
            if (!isMounted) break; // Check if component is still mounted
            
            const value = parseFloat(plaidAccount.current_balance?.toString() || '0') || 0;
            
            if (userPreferredCurrency === 'USD') {
              total += value;
            } else {
              try {
                // Convert from USD to user's preferred currency
                const convertedValue = await convertCurrency(value, 'USD', userPreferredCurrency);
                if (isMounted) {
                  total += convertedValue;
                }
              } catch (conversionError) {
                console.error('Currency conversion failed for Plaid account:', plaidAccount.id, conversionError);
                // Use original value if conversion fails
                if (isMounted) {
                  total += value;
                }
              }
            }
          }
        }
        
        if (isMounted) {
          setTotalValue(total);
        }
      } catch (error) {
        console.error('Error calculating total value:', error);
        // Fallback to simple addition if conversion fails
        if (isMounted) {
          const fallbackTotal = filteredAccounts.reduce((sum: number, account: Account) => {
            const value = parseFloat(account.current_value?.toString() || '0') || 0;
            return sum + value;
          }, 0);
          setTotalValue(fallbackTotal);
        }
      } finally {
        if (isMounted) {
          setTotalValueLoading(false);
        }
      }
    };

    calculateTotalValue();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [filteredAccounts, unlinkedPlaidAccounts, userPreferredCurrency, accountFilter, accounts]);

  // Calculate insights and visualizations with currency conversion
  const [categoryData, setCategoryData] = useState<Array<{name: string, value: number, percentage: number}>>([]);
  const [institutionChartData, setInstitutionChartData] = useState<Array<{institution: string, value: number}>>([]);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [drillDownData, setDrillDownData] = useState<Array<{name: string, value: number, percentage: number}>>([]);
  const [convertedAccountValues, setConvertedAccountValues] = useState<Record<number, number>>({});

  // Calculate category and institution breakdowns with currency conversion
  useEffect(() => {
    const calculateBreakdowns = async () => {
      try {
        const categoryBreakdown: Record<string, number> = {};
        const institutionBreakdown: Record<string, number> = {};
        let calculatedTotal = 0;

        const convertedValues: Record<number, number> = {};
        
        for (const account of filteredAccounts) {
          const value = parseFloat(account.current_value?.toString() || '0') || 0;
          const accountCurrency = account.currency || 'CAD';
          let convertedValue = value;

          if (accountCurrency !== userPreferredCurrency) {
            try {
              convertedValue = await convertCurrency(value, accountCurrency, userPreferredCurrency);
            } catch (conversionError) {
              console.error('Currency conversion failed for account:', account.id, conversionError);
              convertedValue = value;
            }
          }

          convertedValues[account.id] = convertedValue;
          calculatedTotal += convertedValue;

          // Category breakdown - use high-level categories for better insights
          const highLevelCategory = account.high_level_category || 
                                   (account.plaid_subtype ? getHighLevelCategory(account.plaid_subtype) : null) || 
                                   getHighLevelCategoryFromWealthiniCategory(account.category);
          
          if (categoryBreakdown[highLevelCategory]) {
            categoryBreakdown[highLevelCategory] += convertedValue;
          } else {
            categoryBreakdown[highLevelCategory] = convertedValue;
          }

          // Institution breakdown
          const institution = account.institution;
          if (institutionBreakdown[institution]) {
            institutionBreakdown[institution] += convertedValue;
          } else {
            institutionBreakdown[institution] = convertedValue;
          }
        }

        // Create category data
        const categoryDataArray = Object.entries(categoryBreakdown).map(([category, value]) => ({
          name: category,
          value,
          percentage: calculatedTotal > 0 ? (value / calculatedTotal) * 100 : 0
        }));

        // Create institution data
        const institutionDataArray = Object.entries(institutionBreakdown)
          .map(([institution, value]) => ({ institution, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 5);

        setCategoryData(categoryDataArray);
        setInstitutionChartData(institutionDataArray);
        setConvertedAccountValues(convertedValues);
      } catch (error) {
        console.error('Error calculating breakdowns:', error);
        setCategoryData([]);
        setInstitutionChartData([]);
      }
    };

    if (filteredAccounts.length > 0) {
      calculateBreakdowns();
    } else {
      setCategoryData([]);
      setInstitutionChartData([]);
    }
  }, [filteredAccounts, userPreferredCurrency]);

  // Financial insights - calculated from the state-based category data
  const insights = {
    diversification: categoryData.length >= 3 ? 'Good' : categoryData.length >= 2 ? 'Fair' : 'Needs improvement',
    largestCategory: categoryData.length > 0 ? categoryData[0]?.name : 'None',
    largestCategoryPercentage: categoryData.length > 0 ? categoryData[0]?.percentage : 0,
    emergencyFund: categoryData.find(cat => cat.name === 'Cash')?.value || 0,
    investmentRatio: totalValue > 0 && !isNaN(totalValue) ? (categoryData.filter(cat => 
      cat.name === 'Investments'
    ).reduce((sum: number, cat) => sum + cat.value, 0) / totalValue) * 100 : 0
  };

  // Tips and recommendations
  const getTips = () => {
    const tips = [];
    
    if (insights.diversification === 'Needs improvement') {
      tips.push('💡 Consider diversifying across different asset categories to reduce risk');
    }
    
    if (insights.largestCategoryPercentage > 70) {
      tips.push('⚠️ Your portfolio is heavily concentrated in one category. Consider rebalancing');
    }
    
    if (insights.emergencyFund < totalValue * 0.1) {
      tips.push('🛡️ Build up your emergency fund to 3-6 months of expenses');
    }
    
    if (insights.investmentRatio < 20) {
      tips.push('📈 Consider increasing your investment allocation for long-term growth');
    }
    
            if (filteredAccounts.length < 3) {
      tips.push('🎯 Add more accounts to get a complete picture of your financial health');
    }
    
    return tips.length > 0 ? tips : ['🎉 Great job! Your assets are well diversified'];
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  // Drill-down functionality
  const handleCategoryHover = (categoryName: string) => {
    setHoveredCategory(categoryName);
  };

  const handleCategoryLeave = () => {
    setHoveredCategory(null);
  };

  const handleCategoryClick = async (categoryName: string) => {
    if (selectedCategory === categoryName) {
      // Going back to overview
      setSelectedCategory(null);
      setDrillDownData([]);
    } else {
      // Drill down into the category
      setSelectedCategory(categoryName);
      const detailedData = await getDetailedBreakdown(categoryName);
      setDrillDownData(detailedData);
    }
  };

  // Helper function to get converted value for tooltip
  const getConvertedValueForTooltip = (account: Account) => {
    return convertedAccountValues[account.id] || parseFloat(account.current_value?.toString() || '0') || 0;
  };

  const getDetailedBreakdown = async (highLevelCategory: string) => {
    const detailedAccounts: Array<{name: string, value: number, percentage: number}> = [];
    const categoryTotal = categoryData.find(cat => cat.name === highLevelCategory)?.value || 0;

    for (const account of filteredAccounts) {
      const accountHighLevelCategory = account.high_level_category || 
                                      (account.plaid_subtype ? getHighLevelCategory(account.plaid_subtype) : null) || 
                                      getHighLevelCategoryFromWealthiniCategory(account.category);
      
      if (accountHighLevelCategory === highLevelCategory) {
        const value = parseFloat(account.current_value?.toString() || '0') || 0;
        const accountCurrency = account.currency || 'CAD';
        let convertedValue = value;

        if (accountCurrency !== userPreferredCurrency) {
          try {
            convertedValue = await convertCurrency(value, accountCurrency, userPreferredCurrency);
          } catch (conversionError) {
            console.error('Currency conversion failed for account:', account.id, conversionError);
            convertedValue = value;
          }
        }

        detailedAccounts.push({
          name: account.name,
          value: convertedValue,
          percentage: categoryTotal > 0 ? (convertedValue / categoryTotal) * 100 : 0
        });
      }
    }

    return detailedAccounts.sort((a, b) => b.value - a.value);
  };

  const getHoverTooltip = (categoryName: string) => {
    if (selectedCategory) {
      // In drill-down mode, show individual account details
      const drillDownItem = drillDownData.find(item => item.name === categoryName);
      if (drillDownItem) {
        return (
          <div className="hover-tooltip simple">
            <div className="tooltip-title">{drillDownItem.name}</div>
            <div className="tooltip-items">
              <div className="tooltip-item simple">
                <span className="tooltip-item-name">Account Value</span>
                <span className="tooltip-item-value">
                  {formatCurrency(drillDownItem.value, userPreferredCurrency)} ({drillDownItem.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="tooltip-total">
              <span>Account Value: {formatCurrency(drillDownItem.value, userPreferredCurrency)}</span>
            </div>
          </div>
        );
      }
    } else {
      // In overview mode, show category breakdown with individual accounts
      const categoryItem = categoryData.find(cat => cat.name === categoryName);
      if (categoryItem) {
        // Get the accounts that make up this category
        const accountsInCategory = filteredAccounts.filter(account => {
          const accountHighLevelCategory = account.high_level_category || 
                                         (account.plaid_subtype ? getHighLevelCategory(account.plaid_subtype) : null) || 
                                         getHighLevelCategoryFromWealthiniCategory(account.category);
          return accountHighLevelCategory === categoryName;
        });

        return (
          <div className="hover-tooltip simple">
            <div className="tooltip-title">{categoryName}</div>
            <div className="tooltip-items">
              {accountsInCategory.map((account, index) => {
                const convertedValue = getConvertedValueForTooltip(account);
                const percentage = categoryItem.value > 0 ? (convertedValue / categoryItem.value) * 100 : 0;

                return (
                  <div key={index} className="tooltip-item simple">
                    <span className="tooltip-item-name">{account.name}</span>
                    <span className="tooltip-item-value">
                      {formatCurrency(convertedValue, userPreferredCurrency)} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="tooltip-total">
              <span>Total: {formatCurrency(categoryItem.value, userPreferredCurrency)}</span>
            </div>
          </div>
        );
      }
    }
    
    return null;
  };

  // Custom label component for pie chart
  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, value, fill }: any) => {
    const RADIAN = Math.PI / 180;
    // Position labels well outside the pie chart to avoid hover overlap
    const radius = outerRadius + 50;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="start" dominantBaseline="middle">
        <tspan x={x} dy="-0.6em" fontSize="0.9rem" fontWeight="600" fill={fill}>
          {name} {(percent * 100).toFixed(1)}%
        </tspan>
        <tspan x={x} dy="1.4em" fontSize="0.75rem" fontWeight="400" fill="#E5E7EB">
          {formatCurrency(value, userPreferredCurrency)}
        </tspan>
      </text>
    );
  };

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading your assets...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* Hero Section */}
      <div className="assets-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">💎 Your Assets</h1>
            <p className="hero-subtitle">Building wealth, one asset at a time</p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">
                  {totalValueLoading ? 'Calculating...' : 
                   isNaN(totalValue) ? 'Error' : 
                   formatCurrency(totalValue, userPreferredCurrency)}
                </div>
                <div className="stat-label">Total Assets</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{filteredAccounts.length}</div>
                <div className="stat-label">Accounts</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{categoryData.length}</div>
                <div className="stat-label">Categories</div>
              </div>
            </div>
          </div>
          <div className="hero-action">
            <button onClick={openModal} className="btn btn-primary btn-large">
              ➕ Add Asset
            </button>
          </div>
        </div>
      </div>



      {/* Insights Cards */}
      {filteredAccounts.length > 0 && (
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">📊</div>
              <div className="insight-title">Diversification</div>
            </div>
            <div className="insight-value">{insights.diversification}</div>
            <div className="insight-description">
              {insights.diversification === 'Good' ? 'Well diversified portfolio' : 
               insights.diversification === 'Fair' ? 'Moderate diversification' : 
               'Consider adding more categories'}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🎯</div>
              <div className="insight-title">Largest Category</div>
            </div>
            <div className="insight-value">{insights.largestCategory}</div>
            <div className="insight-description">
              {categoryData.length === 0 ? 'Calculating...' : `${insights.largestCategoryPercentage.toFixed(1)}% of your portfolio`}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">📈</div>
              <div className="insight-title">Investment Ratio</div>
            </div>
            <div className="insight-value">
              {categoryData.length === 0 ? 'Calculating...' : `${insights.investmentRatio.toFixed(1)}%`}
            </div>
            <div className="insight-description">
              {categoryData.length === 0 ? 'Calculating...' :
               insights.investmentRatio >= 60 ? 'Excellent growth focus' :
               insights.investmentRatio >= 40 ? 'Good investment allocation' :
               insights.investmentRatio >= 20 ? 'Moderate investments' : 'Consider increasing investments'}
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🛡️</div>
              <div className="insight-title">Emergency Fund</div>
            </div>
            <div className="insight-value">
              {categoryData.length === 0 ? 'Calculating...' : formatCurrency(insights.emergencyFund, userPreferredCurrency)}
            </div>
            <div className="insight-description">
              {categoryData.length === 0 ? 'Calculating...' :
               insights.emergencyFund >= totalValue * 0.1 ? 'Well funded' : 'Consider building up'}
            </div>
          </div>
        </div>
      )}

      {/* Tips Section */}
      {filteredAccounts.length > 0 && (
        <div className="tips-section">
        <h3>💡 Smart Tips for You</h3>
        <div className="tips-list">
          {getTips().map((tip, index) => (
            <div key={index} className="tip-item">
              {tip}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Visualizations */}
      {filteredAccounts.length > 0 && categoryData.length > 0 && (
        <div className="charts-grid">
          <div className="chart-card">
            <h3>
              {selectedCategory ? (
                <div className="chart-header-with-nav">
                  <button 
                    className="back-button"
                    onClick={() => {
                      if (selectedCategory) {
                        handleCategoryClick(selectedCategory);
                      }
                    }}
                  >
                    ← Back to Overview
                  </button>
                  <span>{selectedCategory} Breakdown</span>
                </div>
              ) : (
                'Asset Allocation'
              )}
            </h3>
            <div className={`chart-container ${selectedCategory ? 'transitioning' : ''}`}>
              {selectedCategory ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={drillDownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={CustomLabel}
                      outerRadius={90}
                      innerRadius={30}
                      fill="#8884d8"
                      dataKey="value"
                      onMouseEnter={(data, index) => {
                        handleCategoryHover(data.name);
                      }}
                      onMouseLeave={handleCategoryLeave}
                      onClick={(data) => {
                        handleCategoryClick(data.name);
                      }}
                      animationDuration={300}
                      animationBegin={0}
                    >
                      {(drillDownData).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          className={hoveredCategory === entry.name ? 'hovered' : ''}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value as number, userPreferredCurrency)}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0 && hoveredCategory && selectedCategory) {
                          return getHoverTooltip(hoveredCategory);
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={CustomLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      onMouseEnter={(data, index) => {
                        handleCategoryHover(data.name);
                      }}
                      onMouseLeave={handleCategoryLeave}
                      onClick={(data) => {
                        handleCategoryClick(data.name);
                      }}
                      animationDuration={300}
                      animationBegin={0}
                    >
                      {(categoryData).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          className={hoveredCategory === entry.name ? 'hovered' : ''}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value as number, userPreferredCurrency)}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0 && hoveredCategory && !selectedCategory) {
                          return getHoverTooltip(hoveredCategory);
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="chart-card">
            <h3>Top Institutions</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={institutionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="institution" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value as number, userPreferredCurrency)} />
                  <Bar dataKey="value" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Accounts Table */}
      <div className="card">
        <div className="card-header">
          <h2>Your Assets</h2>
          <div className="header-actions">
            <button onClick={openModal} className="btn btn-primary">
              ➕ Add Asset
            </button>
          </div>
        </div>

        {filteredAccounts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💎</div>
            <h3 className="empty-state-title">No assets yet</h3>
            <p className="empty-state-description">
              Start building your wealth by adding your first asset. Every journey begins with a single step!
            </p>
            <div className="empty-state-tips">
              <h4>💡 Quick Start Ideas:</h4>
              <ul>
                <li>Add your checking and savings accounts</li>
                <li>Include your investment accounts (401k, IRA, etc.)</li>
                <li>Track your real estate investments</li>
                <li>Don't forget about valuable personal property</li>
              </ul>
            </div>
            <button onClick={openModal} className="btn btn-primary">
              ➕ Add Your First Asset
            </button>
          </div>
        ) : (
          <div className="asset-sections">
            {(() => {
              const buckets = getAccountsByBucket();
              const sections = [
                { key: 'banking', title: '🏦 Banking & Investing', icon: '🏦', accounts: buckets.banking },
                { key: 'realEstate', title: '🏠 Real Estate', icon: '🏠', accounts: buckets.realEstate },
                { key: 'vehicles', title: '🚗 Vehicles', icon: '🚗', accounts: buckets.vehicles },
                { key: 'preciousMetals', title: '💰 Precious Metals', icon: '💰', accounts: buckets.preciousMetals },
                { key: 'personalProperty', title: '🎨 Personal Property', icon: '🎨', accounts: buckets.personalProperty },
                { key: 'businessAssets', title: '💼 Business Assets', icon: '💼', accounts: buckets.businessAssets },
                { key: 'cryptocurrency', title: '💰 Cryptocurrency', icon: '₿', accounts: buckets.cryptocurrency },
                { key: 'other', title: '📦 Other Assets', icon: '📦', accounts: buckets.other }
              ];

              return sections.map(section => {
                if (section.accounts.length === 0) return null;
                
                const total = getBucketTotal(section.accounts);
                
                return (
                  <div key={section.key} className="asset-section">
                    <div 
                      className="section-header"
                      onClick={() => toggleSection(section.key)}
                    >
                      <div className="section-title">
                        <span className="section-icon">{section.icon}</span>
                        <h3>{section.title}</h3>
                        <span className="section-total">
                          {formatCurrency(total, userPreferredCurrency)}
                        </span>
                      </div>
                      <div className="section-toggle">
                        {expandedSections[section.key] ? '▼' : '▶'}
                      </div>
                    </div>
                    
                    {expandedSections[section.key] && (
                      <div className="section-content">
                        <div className="table-container">
                          <table className="table">
                            <thead>
                              <tr>
                                {section.key === 'banking' && <th>Institution</th>}
                                {section.key === 'realEstate' && <th>Property Type</th>}
                                {section.key === 'vehicles' && <th>Vehicle Type</th>}
                                {section.key === 'preciousMetals' && <th>Storage Location</th>}
                                {section.key === 'personalProperty' && <th>Item Type</th>}
                                {section.key === 'businessAssets' && <th>Asset Type</th>}
                                {section.key === 'cryptocurrency' && <th>Exchange/Wallet</th>}
                                {section.key === 'other' && <th>Source</th>}
                                {section.key === 'banking' && <th>Account Name</th>}
                                {section.key === 'realEstate' && <th>Property Name</th>}
                                {section.key === 'vehicles' && <th>Vehicle Name</th>}
                                {section.key === 'preciousMetals' && <th>Metal Description</th>}
                                {section.key === 'personalProperty' && <th>Item Name</th>}
                                {section.key === 'businessAssets' && <th>Asset Name</th>}
                                {section.key === 'cryptocurrency' && <th>Coin Description</th>}
                                {section.key === 'other' && <th>Asset Name</th>}
                                <th>Status</th>
                                <th>Category</th>
                                <th>Type</th>
                                <th>Currency</th>
                                <th>Current Value</th>
                                <th>Last Updated</th>
                                <th>Linked To</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.accounts.map((account) => (
                                <tr key={`${section.key}-${account.id}`}>
                                  <td>
                                    {section.key === 'banking' && account.institution}
                                    {section.key === 'realEstate' && (account.institution || 'Property')}
                                    {section.key === 'vehicles' && (account.institution || 'Vehicle')}
                                    {section.key === 'preciousMetals' && (account.institution || 'Home')}
                                    {section.key === 'personalProperty' && (account.institution || 'Item')}
                                    {section.key === 'businessAssets' && (account.institution || 'Asset')}
                                    {section.key === 'cryptocurrency' && (account.institution || 'Exchange')}
                                    {section.key === 'other' && (account.institution || 'Source')}
                                  </td>
                                  <td>{account.name}</td>
                                  <td>
                                    {account.linked_account_id ? (
                                      <span className="badge badge-linked">
                                        🔗 Linked
                                      </span>
                                    ) : (
                                      <span className="badge badge-manual">
                                        📝 Manual
                                      </span>
                                    )}
                                  </td>
                                  <td>{account.category}</td>
                                  <td>
                                    <span className={`badge ${account.account_type}`}>
                                      {account.account_type}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="currency-badge">
                                      {account.currency}
                                    </span>
                                  </td>
                                  <td>
                                    <AccountValue
                                      value={account.current_value || 0}
                                      currency={account.currency}
                                      userPreferredCurrency={userPreferredCurrency}
                                      showOriginal={account.currency !== userPreferredCurrency}
                                    />
                                  </td>
                                  <td>
                                    {account.latest_snapshot_date 
                                      ? formatDate(account.latest_snapshot_date)
                                      : 'Never'
                                    }
                                  </td>
                                  <td>
                                    {account.linked_account_id ? (
                                      <div className="linked-account-info">
                                        <div className="linked-account-header">
                                          {account.plaid_logo_url && (
                                            <img 
                                              src={account.plaid_logo_url} 
                                              alt={account.plaid_institution_name || 'Bank'}
                                              className="linked-account-logo"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                              }}
                                            />
                                          )}
                                          <span className="linked-account-name">
                                            {account.plaid_account_name || 'Plaid Account'}
                                          </span>
                                        </div>
                                        <div className="linked-account-details">
                                          <span className="linked-account-mask">
                                            •••• {account.plaid_account_mask}
                                          </span>
                                          <span className="linked-account-type">
                                            {account.plaid_account_subtype}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="not-linked">Not linked</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="actions">
                                      <button
                                        onClick={() => setUpdatingValueFor(account)}
                                        className="btn btn-primary btn-sm"
                                      >
                                        + Update
                                      </button>
                                      <button
                                        onClick={() => { historyScrollRef.current = window.scrollY; setViewingHistory(account); }}
                                        className="btn btn-secondary btn-sm"
                                      >
                                        📊 History
                                      </button>
                                      {account.linked_account_id && (
                                        <button
                                          onClick={() => handleViewTransactions(account)}
                                          className="btn btn-info btn-sm"
                                        >
                                          💳 Transactions
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleEdit(account)}
                                        className="btn btn-secondary btn-sm"
                                      >
                                        ✏️ Edit
                                      </button>
                                      {account.linked_account_id && (
                                        <button
                                          onClick={() => handleUnlinkAccount(account)}
                                          className="btn btn-warning btn-sm"
                                        >
                                          🔗 Unlink
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDelete(account.id)}
                                        className="btn btn-danger btn-sm"
                                      >
                                        🗑️ Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
        
        {/* Unlinked Plaid Accounts Section */}
        {unlinkedPlaidAccounts.length > 0 && accountFilter === 'all' && (
          <div className="unlinked-plaid-section">
            <h3>🔗 Unlinked Plaid Accounts</h3>
            <p className="section-description">
              These accounts are synced from your bank but not linked to any manual account. 
              Link them to avoid double-counting in your net worth.
            </p>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Institution</th>
                    <th>Account Name</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Current Balance</th>
                    <th>Last Synced</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unlinkedPlaidAccounts.map((account) => (
                    <tr key={`plaid-${account.id}`} className="unlinked-plaid-row">
                      <td>
                        <div className="institution-info">
                          {account.logo_url && (
                            <img 
                              src={account.logo_url} 
                              alt={account.institution_name}
                              className="institution-logo-small"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <span>{account.institution_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="account-info">
                          <span className="account-name">{account.account_name}</span>
                          {account.mask && (
                            <span className="account-mask">•••• {account.mask}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-plaid">
                          🔄 Plaid
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${account.account_type}`}>
                          {account.account_subtype}
                        </span>
                      </td>
                      <td>
                        <span className="balance">
                          {formatCurrency(account.current_balance)}
                        </span>
                      </td>
                      <td>
                        {formatDate(account.last_sync_at)}
                      </td>
                      <td>
                        <button
                          onClick={() => handleLinkPlaidAccount(account)}
                          className="btn btn-primary btn-sm"
                        >
                          🔗 Link to Manual Account
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Motivational Section */}
      <div className="motivation-section">
        <div className="motivation-content">
          <h3>🚀 Your Wealth Journey</h3>
          <p>Every asset you add brings you closer to your financial goals. Keep building, keep growing!</p>
          <div className="motivation-stats">
            <div className="motivation-stat">
                              <div className="motivation-number">{filteredAccounts.length}</div>
              <div className="motivation-label">Assets Tracked</div>
            </div>
            <div className="motivation-stat">
              <div className="motivation-number">{categoryData.length}</div>
              <div className="motivation-label">Categories</div>
            </div>
            <div className="motivation-stat">
              <div className="motivation-number">
                {totalValueLoading ? 'Calculating...' : 
                 isNaN(totalValue) ? 'Error' : 
                 formatCurrency(totalValue, userPreferredCurrency)}
              </div>
              <div className="motivation-label">Total Value</div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Asset modal */}
      <AddAssetModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) setEditingAccount(null);
        }}
        editingAccount={editingAccount}
        onSuccess={async () => {
          const scrollY = window.scrollY;
          await fetchAccounts();
          setTimeout(() => window.scrollTo(0, scrollY), 0);
        }}
        existingAccounts={accounts}
      />

      {viewingHistory && (
        <AccountHistory
          account={viewingHistory}
          onClose={() => {
            setViewingHistory(null);
            const savedScroll = historyScrollRef.current;
            fetchAccounts().then(() => {
              requestAnimationFrame(() => window.scrollTo(0, savedScroll));
            });
          }}
          onUpdate={fetchAccounts}
        />
      )}

      {updatingValueFor && (
        <UpdateValueModal
          account={updatingValueFor}
          open={Boolean(updatingValueFor)}
          onClose={() => setUpdatingValueFor(null)}
          onSaved={async () => {
            await fetchAccounts();
            setUpdatingValueFor(null);
          }}
        />
      )}

      {showLinkModal && selectedPlaidAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Link Plaid Account</h3>
              <button 
                className="close-button" 
                onClick={() => setShowLinkModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Link <strong>{selectedPlaidAccount.account_name}</strong> to a manual account:</p>
              <div className="form-group">
                <label>Select Manual Account:</label>
                <select 
                  className="form-control"
                  value={selectedManualAccountId}
                  onChange={(e) => setSelectedManualAccountId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Choose an account...</option>
                  {accounts
                    .filter(account => !account.linked_account_id) // Only show unlinked accounts
                    .map(account => (
                      <option key={`dropdown-${account.id}`} value={account.id}>
                        {account.name} ({account.institution})
                      </option>
                    ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowLinkModal(false)}
                  disabled={linkingLoading}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleLinkAccount}
                  disabled={linkingLoading || !selectedManualAccountId}
                >
                  {linkingLoading ? 'Linking...' : 'Link Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTransactionsModal && selectedAccountForTransactions && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Transaction History</h3>
              <button 
                className="close-button" 
                onClick={() => setShowTransactionsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="transaction-header">
                <h4>{selectedAccountForTransactions.name}</h4>
                <div className="date-range-selector">
                  <div className="form-group">
                    <label>Start Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={transactionDateRange.startDate}
                      onChange={(e) => setTransactionDateRange({
                        ...transactionDateRange,
                        startDate: e.target.value
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={transactionDateRange.endDate}
                      onChange={(e) => setTransactionDateRange({
                        ...transactionDateRange,
                        endDate: e.target.value
                      })}
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchTransactions(selectedAccountForTransactions.linked_account_id!)}
                    disabled={transactionsLoading}
                  >
                    {transactionsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>
              
              {transactionsLoading ? (
                <div className="loading-container dark-theme">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">Loading transactions...</p>
                </div>
              ) : transactions.length > 0 ? (
                <div className="transactions-list">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction) => (
                        <tr key={transaction.transaction_id}>
                          <td>{transaction.date}</td>
                          <td>{transaction.name}</td>
                          <td>
                            <span className="badge badge-category">
                              {transaction.category?.[0] || 'Uncategorized'}
                            </span>
                          </td>
                          <td className={transaction.amount < 0 ? 'negative' : 'positive'}>
                            {formatCurrency(Math.abs(transaction.amount))}
                          </td>
                          <td>
                            {transaction.pending ? (
                              <span className="badge badge-pending">Pending</span>
                            ) : (
                              <span className="badge badge-completed">Completed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-transactions">
                  <p>No transactions found for the selected date range.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets; 